// Generic function to calculate amount with 3 parameters
export const calculateAmount = (baseAmount, multiplierFactor, amount) => {
  let calculatedAmount = 0;
  if (multiplierFactor && baseAmount) {
    calculatedAmount = (Number(baseAmount) * multiplierFactor) / 100;
  } else if (amount) {
    calculatedAmount = amount;
  }
  return parseFloat(calculatedAmount?.toFixed(8));
};

// Reusable function to calculate total allowances
export const calculateTotalAllowances = (allowances = []) => {
  const totalAllowances = (allowances || []).reduce(
    (total, { amount, base_amount, multiplier_factor_numeric }) => {
      return (
        total + calculateAmount(base_amount, multiplier_factor_numeric, amount)
      );
    },
    0,
  );
  return parseFloat(totalAllowances?.toFixed(8));
};

// Reusable function to calculate total charges
export const calculateTotalCharges = (charges = []) => {
  const totalCharges = (charges || []).reduce(
    (total, { amount, multiplier_factor_numeric, base_amount }) => {
      return (
        total + calculateAmount(base_amount, multiplier_factor_numeric, amount)
      );
    },
    0,
  );
  return parseFloat(totalCharges?.toFixed(8));
};

// Function to calculate taxable and non-taxable amounts with tax breakdown
export const calculateTaxableAmounts = ({
  items = [],
  docCharges = [],
  docAllowances = [],
}) => {
  const chargesBreakdown = {};
  const allowancesBreakdown = {};
  const lineItemsBreakdown = {};
  let totalTaxableAmount = 0;
  let totalNonTaxableAmount = 0;

  // Process line items using lineItemCalculation
  items.forEach((item) => {
    const calculatedItem = lineItemCalculation(item);
    const itemNetAmount = calculatedItem.net_amount;
    const taxRate = item.classified_tax_category?.percent ?? item.tax_rate ?? 0;
    const taxCategoryCode =
      item.classified_tax_category?.id ?? item.tax_category ?? "S";
    const itemTaxAmount = calculatedItem.tax_amount || 0;

    if (taxRate > 0) {
      totalTaxableAmount += itemNetAmount;

      // Add line items to lineItemsBreakdown (grouped by tax category and rate)
      const taxKey = `${taxCategoryCode}-${taxRate}`;
      if (!lineItemsBreakdown[taxKey]) {
        lineItemsBreakdown[taxKey] = {
          category: taxCategoryCode,
          rate: taxRate,
          taxableAmount: 0,
          taxAmount: 0,
        };
      }
      lineItemsBreakdown[taxKey].taxableAmount += itemNetAmount;
      lineItemsBreakdown[taxKey].taxAmount += itemTaxAmount;
    } else {
      totalNonTaxableAmount += itemNetAmount;
    }
  });

  // Process document-level charges
  docCharges.forEach((charge) => {
    // Use the simple 3-parameter function
    const chargeAmount = calculateAmount(
      charge.base_amount,
      charge.multiplier_factor_numeric,
      charge.amount,
    );
    const taxRate = charge.tax_category?.percent || 0;
    const taxCategoryCode = charge.tax_category.id || "S";

    // Group by tax category and rate
    const taxKey = `${taxCategoryCode}-${taxRate}`;
    if (!chargesBreakdown[taxKey]) {
      chargesBreakdown[taxKey] = {
        category: taxCategoryCode,
        rate: taxRate,
        taxableAmount: 0,
        taxAmount: 0,
      };
    }
    chargesBreakdown[taxKey].taxableAmount += chargeAmount;

    if (taxRate > 0) {
      totalTaxableAmount += chargeAmount;
      chargesBreakdown[taxKey].taxAmount += (chargeAmount * taxRate) / 100;
    } else {
      totalNonTaxableAmount += chargeAmount;
    }
  });

  // Process document-level allowances (subtract from taxable amount)
  docAllowances.forEach((allowance) => {
    // Use the simple 3-parameter function
    const allowanceAmount = calculateAmount(
      allowance.base_amount,
      allowance.multiplier_factor_numeric,
      allowance.amount,
    );
    const taxRate = allowance.tax_category?.percent || 0;
    const taxCategoryCode = allowance.tax_category.id || "S";

    // Group by tax category and rate
    const taxKey = `${taxCategoryCode}-${taxRate}`;
    if (!allowancesBreakdown[taxKey]) {
      allowancesBreakdown[taxKey] = {
        category: taxCategoryCode,
        rate: taxRate,
        taxableAmount: 0,
        taxAmount: 0,
      };
    }
    allowancesBreakdown[taxKey].taxableAmount += allowanceAmount; // Store positive value

    if (taxRate > 0) {
      totalTaxableAmount -= allowanceAmount;
      allowancesBreakdown[taxKey].taxAmount +=
        (allowanceAmount * taxRate) / 100; // Store positive value
    } else {
      totalNonTaxableAmount -= allowanceAmount;
    }
  });

  // Round all values
  totalTaxableAmount = Number(totalTaxableAmount.toFixed(2));
  totalNonTaxableAmount = Number(totalNonTaxableAmount.toFixed(2));

  // Round tax breakdown values
  Object.keys(chargesBreakdown).forEach((key) => {
    chargesBreakdown[key].taxableAmount = Number(
      chargesBreakdown[key].taxableAmount.toFixed(2),
    );
    chargesBreakdown[key].taxAmount = Number(
      chargesBreakdown[key].taxAmount.toFixed(2),
    );
  });

  Object.keys(allowancesBreakdown).forEach((key) => {
    allowancesBreakdown[key].taxableAmount = Number(
      allowancesBreakdown[key].taxableAmount.toFixed(2),
    );
    allowancesBreakdown[key].taxAmount = Number(
      allowancesBreakdown[key].taxAmount.toFixed(2),
    );
  });

  Object.keys(lineItemsBreakdown).forEach((key) => {
    lineItemsBreakdown[key].taxableAmount = Number(
      lineItemsBreakdown[key].taxableAmount.toFixed(2),
    );
    lineItemsBreakdown[key].taxAmount = Number(
      lineItemsBreakdown[key].taxAmount.toFixed(2),
    );
  });

  // Calculate final totals from breakdown objects
  let finalTotalTaxableAmount = 0;
  let finalTotalTaxAmount = 0;

  // Process charges (add to totals)
  Object.values(chargesBreakdown).forEach((breakdown) => {
    finalTotalTaxableAmount += breakdown.taxableAmount;
    finalTotalTaxAmount += breakdown.taxAmount;
  });

  // Process allowances (subtract from totals)
  Object.values(allowancesBreakdown).forEach((breakdown) => {
    finalTotalTaxableAmount -= breakdown.taxableAmount;
    finalTotalTaxAmount -= breakdown.taxAmount;
  });

  // Process line items (add to totals)
  Object.values(lineItemsBreakdown).forEach((breakdown) => {
    finalTotalTaxableAmount += breakdown.taxableAmount;
    finalTotalTaxAmount += breakdown.taxAmount;
  });

  // Round final totals
  finalTotalTaxableAmount = Number(finalTotalTaxableAmount.toFixed(2));
  finalTotalTaxAmount = Number(finalTotalTaxAmount.toFixed(2));

  return {
    totalTaxableAmount: finalTotalTaxableAmount,
    totalNonTaxableAmount: totalNonTaxableAmount,
    totalTaxAmount: finalTotalTaxAmount,
    taxBreakdown: {
      allowances: allowancesBreakdown,
      charges: chargesBreakdown,
      lineItems: lineItemsBreakdown,
    },
  };
};

export const lineItemCalculation = (item) => {
  // Forward calculation from price object with backward compatibility
  const baseAmount = item.price?.base_amount || item.price?.price_amount || 0;
  const allowanceAmountFromPrice = item.price?.allowance_amount || 0;
  const baseQuantity = item.price?.base_quantity || 1;

  // Calculate price_amount = base_amount - allowance_amount
  const priceAmount = baseAmount - allowanceAmountFromPrice;

  // Calculate baseAmount = (price_amount / base_quantity) * quantity
  const calculatedBaseAmount = (priceAmount / baseQuantity) * item.quantity;

  // Line item level allowances and charges
  const allowanceAmount = calculateTotalAllowances(item?.allowances);
  const chargeAmounts = calculateTotalCharges(item?.charges);

  const netAmount = calculatedBaseAmount - allowanceAmount + chargeAmounts;

  let taxAmount = 0;
  if (item?.classified_tax_category?.percent) {
    taxAmount = (netAmount * item?.classified_tax_category?.percent) / 100;
  }

  const totalAmount = netAmount + taxAmount;

  return {
    ...item,
    allowance_amount: Number(allowanceAmount || 0),
    price: item.price,
    unit_price: Number(item.unit_price || 0),
    quantity: Number(item.quantity || 0),
    base_amount: Number(calculatedBaseAmount?.toFixed(2)),
    net_amount: Number(netAmount?.toFixed(2)),
    charge_amount: Number(chargeAmounts?.toFixed(8)),
    tax_amount: Number(taxAmount?.toFixed(2)),
    total_amount: Number(totalAmount?.toFixed(2)),
    tax_rate: Number(item?.tax_category?.percent || 0),
  };
};

const pricingEngineForDocuments = ({
  items = [],
  docCharges = [],
  docAllowances = [],
  conversionRatePerOMR,
  payableRoundingAmount,
  prepaidAmount,
}) => {
  if (!Array.isArray(items)) {
    throw new Error("Expected 'items' to be an array.");
  }

  const itemsSubtotal = items.reduce((acc, item) => {
    // Use forward calculation: price_amount = base_amount - allowance_amount (with backward compatibility)
    const baseAmount = item.price?.base_amount || item.price?.price_amount || 0;
    const allowanceAmountFromPrice = item.price?.allowance_amount || 0;
    const priceAmount = baseAmount - allowanceAmountFromPrice;
    const baseQuantity = item.price?.base_quantity || 1;
    const calculatedAmount = (priceAmount / baseQuantity) * item.quantity;
    return acc + calculatedAmount;
  }, 0);

  const totalItemAllowances = items.reduce((acc, item) => {
    const { allowance_amount } = lineItemCalculation(item);
    return acc + allowance_amount;
  }, 0);

  const totalItemCharges = items.reduce((acc, item) => {
    const { charge_amount } = lineItemCalculation(item);
    return acc + charge_amount;
  }, 0);

  const totalDocumentAllowances = calculateTotalAllowances(docAllowances);
  const totalDocumentCharges = calculateTotalCharges(docCharges);

  const headerCharges = calculateTotalCharges(docCharges);
  const headerAllowance = calculateTotalAllowances(docAllowances);

  const subtotal = itemsSubtotal - totalItemAllowances + totalItemCharges;
  const totalExcludingTax = subtotal - headerAllowance + headerCharges;

  // Calculate taxable and non-taxable amounts
  const {
    totalTaxableAmount,
    totalNonTaxableAmount,
    totalTaxAmount,
    taxBreakdown,
  } = calculateTaxableAmounts({
    items,
    docCharges,
    docAllowances,
    itemsSubtotal,
    totalItemAllowances,
    totalItemCharges,
    headerCharges,
    headerAllowance,
  });
  const totalIncludingTax = totalExcludingTax + Number(totalTaxAmount);
  const totalPayableAmount =
    totalIncludingTax - (prepaidAmount || 0) + (payableRoundingAmount || 0);

  return {
    itemsSubtotal: Number(itemsSubtotal.toFixed(2)),
    subtotal: Number(subtotal.toFixed(2)),
    charges: docCharges,
    allowances: docAllowances,
    headerCharges: Number(headerCharges.toFixed(2)),
    headerAllowance: Number(headerAllowance.toFixed(2)),
    totalTaxAmount: Number(totalTaxAmount.toFixed(2)),
    taxInOMR: conversionRatePerOMR
      ? Number((totalTaxAmount * conversionRatePerOMR).toFixed(2))
      : null,
    totalItemAllowances: Number(totalItemAllowances.toFixed(2)),
    totalItemCharges: Number(totalItemCharges.toFixed(2)),
    totalDocumentAllowances: Number(totalDocumentAllowances.toFixed(2)),
    totalDocumentCharges: Number(totalDocumentCharges.toFixed(2)),
    totalExcludingTax: Number(totalExcludingTax.toFixed(2)),
    totalIncludingTax: Number(totalIncludingTax.toFixed(2)),
    totalPayableAmount: Number(totalPayableAmount.toFixed(2)),
    totalPaid: 0, //Todo :- implement total paid later
    totalTaxableAmount: totalTaxableAmount,
    totalNonTaxableAmount: totalNonTaxableAmount,
    taxBreakdown: taxBreakdown,
    payableRoundingAmount: payableRoundingAmount
      ? Number(payableRoundingAmount.toFixed(2))
      : null,
  };
};

export default pricingEngineForDocuments;
